/**
 * Tester surface — contract types for the capability-testing runner.
 *
 * The Tester lets an operator point an autonomous test agent at one of our
 * own servers (today: a C1 / conferweb box) and exercise its value-added
 * services end-to-end — starting with **Mass Notification** (place a real
 * call / send a real SMS to a chosen phone number and watch the outcome).
 *
 * Two halves:
 *   1. **Targets** — the operator registers a server + its API keys once
 *      (System: persisted server-side, secrets write-only). See
 *      `TesterTarget` / `CreateTesterTargetRequest`.
 *   2. **Runs** — the operator picks a target + a service + channels +
 *      a destination phone, and the server drives the upstream API calls
 *      (create → configure → launch) automatically. See
 *      `RunMassNotificationTestRequest` / `TesterRunResult`.
 *
 * The actual C1 wire contract (verified against the conferweb source) lives
 * server-side in `apps/server/src/conferweb/massNotify.ts`; the one piece of
 * pure request-shaping that is worth unit-testing in isolation —
 * `buildMassNotificationCreateBody` — lives here so both the server and its
 * tests share one definition.
 */

/** Kind of upstream a test target speaks to. Only conferweb today; the
 * union leaves room for future product servers without a schema change. */
export type TesterTargetKind = "conferweb";

export const TESTER_TARGET_KINDS: readonly TesterTargetKind[] = ["conferweb"];

/**
 * A configured test target as returned to the client. Secrets are NEVER
 * echoed back — the booleans (`has_*`) only tell the UI whether a secret is
 * currently stored, so an edit form can show "•••• set" without leaking it.
 */
export interface TesterTarget {
  id: number;
  name: string;
  kind: TesterTargetKind;
  /** Base URL of the upstream, e.g. `https://c1.conferweb.com`. */
  api_base: string;
  /** Service-moderator login that will OWN the created notifications. */
  service_email: string;
  /** E.164 caller-ID the carrier presents on dial-out (C1 `calling_number`). */
  dialout_caller_id: string;
  has_account_api_key: boolean;
  has_service_password: boolean;
  has_webcast_api_key: boolean;
  created_at: string;
  updated_at: string;
}

export interface TesterTargetsList {
  targets: TesterTarget[];
}

export interface CreateTesterTargetRequest {
  name: string;
  kind?: TesterTargetKind;
  api_base: string;
  /** `Account-Manager-Api-Key` app-gate key (server-side secret). */
  account_api_key: string;
  service_email: string;
  service_password: string;
  /** Public webcast key; optional — a working default is baked server-side. */
  webcast_api_key?: string;
  /** Defaults to {@link DEFAULT_DIALOUT_CALLER_ID} when omitted. */
  dialout_caller_id?: string;
}

/**
 * Patch an existing target. Any field left `undefined` is unchanged.
 * Secret fields additionally treat the empty string as "leave unchanged"
 * (the edit form sends "" for a secret the operator didn't re-type).
 */
export interface UpdateTesterTargetRequest {
  name?: string;
  api_base?: string;
  account_api_key?: string;
  service_email?: string;
  service_password?: string;
  webcast_api_key?: string;
  dialout_caller_id?: string;
}

/** Result of a connectivity check against a stored target. */
export interface TesterTargetCheckResult {
  ok: boolean;
  /** Did `/auth/login` succeed (account key + service creds valid)? */
  login_ok: boolean;
  /** Did the mass-notification list endpoint answer (feature enabled +
   * session accepted)? `null` when the check stopped at login. */
  feature_ok: boolean | null;
  message: string;
}

/**
 * Value-added services the tester can exercise. `mass_notification` is the
 * only one wired today; the rest are declared so the UI can show the full
 * roadmap (rendered disabled until each lands).
 */
export const TESTER_SERVICES = [
  "mass_notification",
  "conferencing",
  "two_way_sms",
  "whatsapp",
] as const;
export type TesterService = (typeof TESTER_SERVICES)[number];

export const TESTER_SERVICE_LABELS: Record<TesterService, string> = {
  mass_notification: "Mass Notification",
  conferencing: "Conferencing",
  two_way_sms: "Two-way SMS",
  whatsapp: "WhatsApp",
};

/** Services that are actually executable today. */
export const TESTER_LIVE_SERVICES: readonly TesterService[] = [
  "mass_notification",
];

/**
 * Delivery channels a mass-notification test can light up. Each maps to a
 * C1 `output_media_*` flag on the created notification.
 */
export const TESTER_CHANNELS = ["voice", "sms", "whatsapp", "email", "push"] as const;
export type TesterChannel = (typeof TESTER_CHANNELS)[number];

export const TESTER_CHANNEL_LABELS: Record<TesterChannel, string> = {
  voice: "Voice call-out",
  sms: "SMS",
  whatsapp: "WhatsApp",
  email: "Email",
  push: "Browser push",
};

/** The XOP Twilio DID that must be the dial-out caller ID for a call to
 * actually ring (the carrier drops a leg with no valid CLI). */
export const DEFAULT_DIALOUT_CALLER_ID = "+18287608073";

export interface RunMassNotificationTestRequest {
  target_id: number;
  service: "mass_notification";
  /** At least one channel. Order is irrelevant. */
  channels: TesterChannel[];
  /** Defaults to {@link DEFAULT_DIALOUT_CALLER_ID}. */
  caller_id?: string;
  /** Ad-hoc recipient phone in E.164, e.g. `+37126030544`. */
  target_phone: string;
  /** Ad-hoc recipient email — REQUIRED for the email channel to reach anyone
   * (the email is sent to the recipient's `email1`). */
  target_email?: string;
  target_name?: string;
  subject?: string;
  /** Text body for sms / whatsapp / email / push channels. */
  message?: string;
  /** Optional pre-recorded voice message id for the voice channel. When
   * omitted, C1 may reject a pure-voice blast that has no audio. */
  alert_message_id?: number;
  /** Simultaneous dial-out ports (C1 "Maximum Ports" / `max_channels`).
   * MUST be a positive integer for a voice blast — C1 rejects 0/blank with
   * "Invalid value for Maximum Ports". Defaults to 1 (one recipient = one
   * port). Keep it ≤ the server's licensed phone-line ports. */
  max_channels?: number;
}

/** One step of a run, surfaced for diagnostics. `voice_message` is the
 * optional TTS-audio creation that precedes `create` when voice is selected. */
export interface TesterRunStep {
  name: "login" | "voice_message" | "create" | "start";
  ok: boolean;
  http_status?: number;
  detail?: string;
}

export interface TesterRunResult {
  id: number;
  target_id: number;
  target_name: string;
  service: TesterService;
  channels: TesterChannel[];
  target_phone: string;
  caller_id: string;
  ok: boolean;
  /** The C1 service id of the created notification, when create succeeded. */
  notification_id?: number;
  steps: TesterRunStep[];
  /** `launch_media` notes (e.g. "Voice messages started"). */
  notes: string[];
  /** `launch_media` errors (surfaced verbatim). */
  errors: string[];
  message: string;
  created_at: string;
  created_by_email?: string;
}

export interface TesterRunsList {
  runs: TesterRunResult[];
}

// ---------------------------------------------------------------------------
// Pure request-shaping
// ---------------------------------------------------------------------------

/** The exact JSON body C1 `POST /api/v2/mass_notifications/create/` expects.
 * `call_out_configuration`, `email_config` and `sms_config` are all
 * `params.require(...)` upstream, so they MUST be present even when their
 * `output_media_*` flag is false — omitting any one is a 500. */
export interface MassNotificationCreateBody {
  subject: string;
  send_to: "all" | "uncontacted" | "contacted";
  call_out_configuration: {
    output_media_voice: boolean;
    caller_id: string;
    /** Simultaneous dial-out ports — must be > 0 for a voice blast. */
    max_channels: number;
    alert_message_id?: number;
  };
  email_config: {
    output_media_email: boolean;
    email_text: string;
    email_format: "plain" | "html";
  };
  sms_config: {
    output_media_sms: boolean;
    sms_text: string;
  };
  whatsapp_config: {
    output_media_whatsapp: boolean;
    whatsapp_text: string;
  };
  push_config: {
    output_media_push: boolean;
    push_text: string;
  };
  adhoc_users_attributes: Array<{
    f_name: string;
    l_name: string;
    phone1: string;
    /** Recipient email — the email channel sends here. Omitted when blank. */
    email1?: string;
  }>;
}

/**
 * Shape the C1 create body from a run request. Pure + deterministic so the
 * server and the test suite agree on every flag.
 *
 *  - All three required config blocks are always present.
 *  - `caller_id` (→ C1 `calling_number`) is always set; it only matters for
 *    voice but is harmless otherwise.
 *  - The destination phone is added as a single ad-hoc recipient.
 *  - `send_to: "all"` so the ad-hoc member is dialled regardless of any
 *    "already contacted" bookkeeping.
 *  - Pins are intentionally omitted so C1 auto-generates them.
 */
export function buildMassNotificationCreateBody(
  req: RunMassNotificationTestRequest,
): MassNotificationCreateBody {
  const channels = new Set(req.channels);
  const callerId = (req.caller_id?.trim() || DEFAULT_DIALOUT_CALLER_ID).trim();
  const subject = req.subject?.trim() || "XOP Tester — Mass Notification";
  const message =
    req.message?.trim() || "This is an automated XOP capability test.";
  const name = (req.target_name?.trim() || "XOP Tester").split(/\s+/);
  const fName = name[0] || "XOP";
  const lName = name.slice(1).join(" ") || "Tester";
  // "Maximum Ports" — C1 rejects 0/blank for a voice blast. One recipient
  // needs one port; default to 1 and floor any positive override.
  const maxChannels =
    req.max_channels && req.max_channels > 0 ? Math.floor(req.max_channels) : 1;

  return {
    subject,
    send_to: "all",
    call_out_configuration: {
      output_media_voice: channels.has("voice"),
      caller_id: callerId,
      max_channels: maxChannels,
      ...(req.alert_message_id !== undefined
        ? { alert_message_id: req.alert_message_id }
        : {}),
    },
    email_config: {
      output_media_email: channels.has("email"),
      email_text: message,
      email_format: "plain",
    },
    sms_config: {
      output_media_sms: channels.has("sms"),
      sms_text: message,
    },
    whatsapp_config: {
      output_media_whatsapp: channels.has("whatsapp"),
      whatsapp_text: message,
    },
    push_config: {
      output_media_push: channels.has("push"),
      push_text: message,
    },
    adhoc_users_attributes: [
      {
        f_name: fName,
        l_name: lName,
        phone1: req.target_phone.trim(),
        ...(req.target_email?.trim()
          ? { email1: req.target_email.trim() }
          : {}),
      },
    ],
  };
}
